import { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { IChart } from "../../../../../@types/chart.type";
import iconDelete from "./../../../../../assets/_TrashIcon_gray.svg";
import { storageUserGet } from "../../../../../storage/storageUser";
import { getPositionCapacity } from "../../../../../services/api/widgets/widgetsService";
import { IApiResponse } from "../../../../../@types/api-response.type";
import { IPositionCapacity } from "../../../../../@types/widgets.type";
import iconPosition from "../../../../../assets/icon-config_position.svg";
import styles from "./styles.module.scss";

interface PositionCapacityProps {
  chart: IChart;
  ismodeEdit: boolean;
  deleteChart: (id: string | undefined, widgetId: string) => void;
  rowHeightGrid: number;
}

export function PositionCapacity({
  chart,
  deleteChart,
  ismodeEdit,
  rowHeightGrid,
}: PositionCapacityProps) {
  const [data, setData] = useState({} as IPositionCapacity);
  const [loading, setLoading] = useState(true);

  const user = storageUserGet();

  useEffect(() => {
    async function fetchPositionCapacity() {
      try {
        setLoading(true);
        const response = (await getPositionCapacity(
          user.email,
          user?.resourceId as string
        )) as IApiResponse<IPositionCapacity>;

        if (response.statusCode === 200) {
          setData(response.result);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }
    fetchPositionCapacity();
  }, [user.email, user?.resourceId]);

  const colors = useMemo(() => {
    return data?.items?.map((item, index) => {
      const predefinedColors = [
        "#00E396",
        "#008FFB",
        "#FEB019",
        "#FF4560",
        "#775DD0",
        "#3F51B5",
        "#546E7A",
        "#D4526E",
        "#8D5B4C",
        "#F86624",
        "#A5978B",
        "#2E294E",
        "#1B998B",
        "#E71D36",
        "#FF9F1C",
        "#5F0F40",
        "#9A031E",
        "#FB8B24",
        "#E36414",
        "#0F4C5C",
        "#7EBDC2",
        "#C44536",
        "#772E25",
        "#B3A394",
        "#D6CDA4",
        "#5D737E",
        "#64B6AC",
        "#A2C11C",
        "#F9DC5C",
        "#247BA0",
      ];
      return predefinedColors[index % predefinedColors.length];
    });
  }, [data]);

  const calculatePercentage = useMemo(() => {
    return data?.items?.map(({ usageHours }) => {
      return data.capacityTotal > 0
        ? Number(((usageHours / data.capacityTotal) * 100).toFixed(0))
        : 0;
    });
  }, [data]);

  const postions = data?.items?.map((value) => value.position);

  const series = [
    {
      data: calculatePercentage,
    },
  ];

  const options = {
    chart: {
      type: "bar",
      width: "100%",
      height: { rowHeightGrid },
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
    colors: colors,
    plotOptions: {
      bar: {
        borderRadius: 4,
        borderRadiusApplication: "end",
        horizontal: true,
        barHeight: "30%",
      },
    },
    dataLabels: {
      enabled: false,
    },
    labels: postions,
    xaxis: {
      min: 0,
      labels: {
        formatter: (value: number) => `${value}%`,
        style: {
          fontSize: "12px",
          fontFamily: "Archivo, sans-serif",
          fontWeight: 500,
          colors: ["#616161"],
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (value: string) => {
          return value.length > 10 ? value.slice(0, 10) + "..." : value;
        },
        style: {
          fontSize: "12px",
          fontFamily: "Archivo, sans-serif",
          fontWeight: 500,
          colors: ["#616161"],
        },
      },
    },
    tooltip: {
      custom: function ({
        dataPointIndex,
      }: {
        dataPointIndex: number;
      }) {
        const { position, usageHours } = data.items[dataPointIndex];

        return `
          <div class='labelCustom'>
            <span><b>${position}</b></span>
            <span>Utilização: ${Number(
              ((usageHours / data.capacityTotal) * 100).toFixed(0)
            )}%</span>
        
          </div>
        `;
      },
    },
  };

  return (
    <div className={styles.widget}>
      <div className={styles.header}>
        <h3>
          <img src={iconPosition} alt="" />
          Capacity Por Função
        </h3>

        <div>
          <span>Média de utilização</span>
          <b>{data?.usagePercentage?.toFixed(0)}%</b>
        </div>
      </div>
      <div style={{ width: "100%", height: "100%" }}>
        {loading ? (
          <p style={{ textAlign: "center", color: "#212121" }}>Carregando...</p>
        ) : (
          <>
            {data?.items?.length ? (
              <ReactApexChart
                options={options}
                series={series}
                type="bar"
                width="100%"
                height={rowHeightGrid - 56}
              />
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "2rem",
                  color: "#616161",
                }}
              >
                <p style={{ marginTop: "1rem" }}>Nenhum dado encontrado.</p>
              </div>
            )}
          </>
        )}
      </div>
      {ismodeEdit && (
        <button onClick={() => deleteChart(chart.id, chart.widgetId)}>
          <img src={iconDelete} alt="" />
        </button>
      )}
    </div>
  );
}
